<template>
  <div class="max-w-2xl mx-auto">
    <h1 class="text-3xl font-bold text-nirvana-blue text-center mb-8">
      Upload Your File
    </h1>

    <form @submit.prevent="uploadFile" class="dollhouse-card space-y-6">
      <div>
        <label class="block text-gray-700 mb-2">
          Choose a file to upload *
        </label>
        <SpecialUpload
          ref="specialUpload"
          @fileSelected="handleFileSelected"
          :disabled="submitting"
        />
      </div>

      <div
        v-if="error"
        class="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4"
      >
        {{ error }}
      </div>

      <div v-if="fileUrl" class="bg-green-50 border border-green-200 text-green-600 rounded-lg p-4">
        <p class="mb-2">File uploaded successfully!</p>
        <div class="flex">
          <input 
            type="text" 
            :value="fileUrl" 
            readonly 
            class="form-input flex-grow"
          />
          <button 
            @click="copyToClipboard" 
            class="ml-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
          >
            Copy
          </button>
        </div>
      </div>

      <div class="flex justify-end space-x-4">
        <button
          type="button"
          class="text-gray-500 hover:text-gray-700 transition-colors py-2 px-4"
          @click="resetForm"
          :disabled="submitting"
        >
          Reset
        </button>
        <button
          type="submit"
          class="dollhouse-button"
          :disabled="!file || submitting"
        >
          <template v-if="submitting">
            <LoadingSpinner class="w-5 h-5" />
            Uploading...
          </template>
          <template v-else>
            Upload File
          </template>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { toast } from 'vue3-toastify';
import SpecialUpload from '@/components/SpecialUpload.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const specialUpload = ref(null);
const file = ref(null);
const fileUrl = ref('');
const submitting = ref(false);
const error = ref(null);

const handleFileSelected = (selectedFile) => {
  file.value = selectedFile;
  error.value = null;
};

const uploadFile = async () => {
  if (!file.value || !specialUpload.value) {
    error.value = 'Please select a file first.';
    return;
  }

  submitting.value = true;
  error.value = null;

  try {
    const url = await specialUpload.value.uploadFile();
    
    if (url) {
      fileUrl.value = url;
      toast.success('File uploaded successfully!');
    } else {
      throw new Error('Failed to get upload URL');
    }
  } catch (e) {
    error.value = e.message || 'Failed to upload file';
    toast.error('Failed to upload file');
  } finally {
    submitting.value = false;
  }
};

const copyToClipboard = () => {
  navigator.clipboard.writeText(fileUrl.value);
  toast.info('Link copied to clipboard!');
};

const resetForm = () => {
  file.value = null;
  fileUrl.value = '';
  error.value = null;
  if (specialUpload.value && specialUpload.value.reset) {
    specialUpload.value.reset();
  }
};
</script>

<style scoped>
.form-input {
  @apply w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-nirvana-blue focus:border-transparent;
}

.dollhouse-card {
  @apply bg-white rounded-lg shadow-md p-6 border border-gray-100;
}

.dollhouse-button {
  @apply bg-nirvana-blue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed;
}
</style>
